import React from 'react';
import { Box, Container, Grid, Typography, Link, IconButton } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { themeColors } from '../App';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import TwitterIcon from '@mui/icons-material/Twitter';
import FacebookIcon from '@mui/icons-material/Facebook';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <Box
      sx={{
        bgcolor: '#f8f9fa',
        pt: 8,
        pb: 6,
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '100%',
          background: 'radial-gradient(circle at top right, rgba(76, 175, 80, 0.03) 0%, transparent 70%)',
          zIndex: 1
        }
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Typography variant="h6" sx={{ color: themeColors.primary, fontWeight: 600, mb: 2 }}>
              About Vibhuti Industries
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3, lineHeight: 1.8 }}>
              A leading manufacturer in precision engineering, specializing in defense and aerospace components. 
              ISO 9001:2015 certified with over 4 years of excellence in manufacturing.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <IconButton 
                component="a" 
                href="https://linkedin.com" 
                target="_blank"
                sx={{ 
                  color: themeColors.primary,
                  '&:hover': { 
                    bgcolor: 'rgba(76, 175, 80, 0.08)',
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                <LinkedInIcon />
              </IconButton>
              <IconButton 
                component="a" 
                href="https://twitter.com" 
                target="_blank"
                sx={{ 
                  color: themeColors.primary,
                  '&:hover': { 
                    bgcolor: 'rgba(76, 175, 80, 0.08)',
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                <TwitterIcon />
              </IconButton>
              <IconButton 
                component="a" 
                href="https://facebook.com" 
                target="_blank"
                sx={{ 
                  color: themeColors.primary,
                  '&:hover': { 
                    bgcolor: 'rgba(76, 175, 80, 0.08)',
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                <FacebookIcon />
              </IconButton>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="h6" sx={{ color: themeColors.primary, fontWeight: 600, mb: 2 }}>
              Quick Links
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {[
                { text: 'Home', path: '/' },
                { text: 'About Us', path: '/about' },
                { text: 'Infrastructure', path: '/infrastructure' },
                { text: 'Products', path: '/products' },
                { text: 'Contact Us', path: '/contact' }
              ].map((link) => (
                <Link
                  key={link.text}
                  component={RouterLink}
                  to={link.path}
                  sx={{
                    color: 'text.secondary',
                    textDecoration: 'none',
                    '&:hover': {
                      color: themeColors.primary,
                      textDecoration: 'none'
                    }
                  }}
                >
                  {link.text}
                </Link>
              ))}
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="h6" sx={{ color: themeColors.primary, fontWeight: 600, mb: 2 }}>
              Contact Information
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <LocationOnIcon sx={{ color: themeColors.primary }} />
                <Link
                  href="https://maps.google.com/?q=E-143+Sector-63+Noida+Uttar+Pradesh+201301"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: 'text.secondary',
                    textDecoration: 'none',
                    '&:hover': {
                      color: themeColors.primary,
                      textDecoration: 'underline'
                    }
                  }}
                >
                  <Typography variant="body2" sx={{ color: 'inherit' }}>
                    E-143, Sector-63, Noida,<br />
                    Gautam Buddha Nagar, Uttar Pradesh - 201301, India
                  </Typography>
                </Link>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <PhoneIcon sx={{ color: themeColors.primary }} />
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Link
                    href="tel:+919999922657"
                    sx={{
                      color: 'text.secondary',
                      textDecoration: 'none',
                      '&:hover': {
                        color: themeColors.primary,
                        textDecoration: 'underline'
                      }
                    }}
                  >
                    <Typography variant="body2" sx={{ color: 'inherit' }}>
                      +91-9999922657
                    </Typography>
                  </Link>
                  <Link
                    href="tel:+919667674397"
                    sx={{
                      color: 'text.secondary',
                      textDecoration: 'none',
                      '&:hover': {
                        color: themeColors.primary,
                        textDecoration: 'underline'
                      }
                    }}
                  >
                    <Typography variant="body2" sx={{ color: 'inherit' }}>
                      +91-9667674397
                    </Typography>
                  </Link>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <EmailIcon sx={{ color: themeColors.primary }} />
                <Link
                  href="mailto:office@thevibuti.com"
                  sx={{
                    color: 'text.secondary',
                    textDecoration: 'none',
                    '&:hover': {
                      color: themeColors.primary,
                      textDecoration: 'underline'
                    }
                  }}
                >
                  <Typography variant="body2" sx={{ color: 'inherit' }}>
                    office@thevibuti.com
                  </Typography>
                </Link>
              </Box>
            </Box>
          </Grid>
        </Grid>

        <Box 
          sx={{ 
            mt: 8, 
            pt: 3, 
            borderTop: '1px solid rgba(0, 0, 0, 0.1)',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2
          }}
        >
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {currentYear} Vibhuti Industries (India) Pvt. Ltd. All rights reserved. | ISO 9001:2015 Certified Company
          </Typography>
          <Box sx={{ display: 'flex', gap: 3 }}>
            <Link
              href="/privacy-policy"
              sx={{
                color: 'text.secondary',
                textDecoration: 'none',
                fontSize: '0.875rem',
                '&:hover': {
                  color: themeColors.primary
                }
              }}
            >
              Privacy Policy
            </Link>
            <Link
              href="/terms"
              sx={{
                color: 'text.secondary',
                textDecoration: 'none',
                fontSize: '0.875rem',
                '&:hover': {
                  color: themeColors.primary
                }
              }}
            >
              Terms of Use
            </Link>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
